import React, { useState } from 'react';
import toast from 'react-hot-toast';
import useRefund from '../../jetSetter/hooks/useRefund';

const RefundApprovalModal = ({ refund, approvalType, isOpen, onClose, onSuccess }) => {
  const [approvalStatus, setApprovalStatus] = useState('Approved');
  const [reason, setReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { approveRefundAsSubAdmin, approveRefundAsAdmin } = useRefund();

  if (!isOpen || !refund) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setIsSubmitting(true);

    try {
      let result;

      if (approvalType === 'subadmin') {
        result = await approveRefundAsSubAdmin(refund.id, approvalStatus, reason, adminNotes);
      } else if (approvalType === 'admin') {
        result = await approveRefundAsAdmin(refund.id, approvalStatus, reason, adminNotes);
      } else {
        // View only mode
        onClose();
        return;
      }

      if (result.success) {
        const msg = result.message || `Refund ${approvalStatus.toLowerCase()} successfully!`;
        if (msg.toLowerCase().includes('payment failed')) {
          toast.error(msg, { duration: 8000 });
        } else {
          toast.success(msg);
        }
        setApprovalStatus('Approved');
        setReason('Customer Request');
        setAdminNotes('');
        onClose();
        if (onSuccess) onSuccess();
      } else {
        toast.error(result.message || 'Failed to process approval');
      }
    } catch (error) {
      toast.error('An error occurred');
      console.error('Approval error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isViewOnly = approvalType === 'view';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-jet-yellow to-orange-400 px-6 py-4 sticky top-0">
          <h2 className="text-xl font-bold text-white">
            {isViewOnly ? 'Refund Details' : `${approvalType === 'subadmin' ? 'SubAdmin' : 'Admin'} Approval`}
          </h2>
          <p className="text-sm text-white/90">Booking: {refund.booking_reference}</p>
          {!isViewOnly && approvalType === 'subadmin' && Number(refund.approvals_required) >= 2 && (
            <p className="text-xs text-white/90 mt-1">
              Refunds over $5,000 require Admin approval after your decision.
            </p>
          )}
          {!isViewOnly && approvalType === 'admin' && (
            <p className="text-xs text-white/90 mt-1">
              Your decision is final — no SubAdmin review is required.
            </p>
          )}
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Refund Details */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h3 className="font-semibold text-gray-900">Refund Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Jetsetter</p>
                <p className="font-semibold">{refund.user_name}</p>
              </div>
              <div>
                <p className="text-gray-600">Email</p>
                <p className="font-semibold">{refund.user_email}</p>
              </div>
              <div>
                <p className="text-gray-600">Original Amount</p>
                <p className="font-semibold">{formatCurrency(refund.original_amount)}</p>
              </div>
              <div>
                <p className="text-gray-600">Refund Amount</p>
                <p className="font-semibold text-green-600">{formatCurrency(refund.refund_amount)}</p>
              </div>
              <div>
                <p className="text-gray-600">Cancellation Fee</p>
                <p className="font-semibold text-red-600">
                  {formatCurrency(refund.original_amount - refund.refund_amount)}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Status</p>
                <p className="font-semibold">{refund.status}</p>
              </div>
              <div>
                <p className="text-gray-600">Requested</p>
                <p className="font-semibold text-xs">{formatDate(refund.requested_at)}</p>
              </div>
              <div>
                <p className="text-gray-600">Approvals</p>
                <p className="font-semibold">{refund.approvals_received}/{refund.approvals_required}</p>
              </div>
            </div>
          </div>

          {/* Cancellation Reason */}
          {refund.cancellation_reason && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Reason for Refund:</span> {refund.cancellation_reason}
              </p>
            </div>
          )}

          {/* Approvals History */}
          {refund.approvals && refund.approvals.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <h3 className="font-semibold text-gray-900">Approval History</h3>
              {refund.approvals.map((approval, idx) => (
                <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">
                      {approval.approval_type === 'SubAdmin' ? '👤 SubAdmin' : '👨‍💼 Admin'} #{idx + 1}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      approval.approval_status === 'Approved'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {approval.approval_status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">Reason: {approval.reason}</p>
                  {approval.admin_notes && (
                    <p className="text-xs text-gray-600 mt-1">Notes: {approval.admin_notes}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{formatDate(approval.approved_at)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Approval Form (if not view only) */}
          {!isViewOnly && (
            <>
              {/* Approval Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Decision <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="Approved"
                      checked={approvalStatus === 'Approved'}
                      onChange={(e) => setApprovalStatus(e.target.value)}
                      disabled={isSubmitting}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Approve</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="Rejected"
                      checked={approvalStatus === 'Rejected'}
                      onChange={(e) => setApprovalStatus(e.target.value)}
                      disabled={isSubmitting}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Reject</span>
                  </label>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Customer Request, Policy Violation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-jet-yellow focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any additional notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-jet-yellow focus:border-transparent resize-none"
                  rows="3"
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isViewOnly ? 'Close' : 'Cancel'}
            </button>
            {!isViewOnly && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-jet-yellow text-white font-medium rounded-md hover:bg-yellow-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check"></i>
                    Submit Decision
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RefundApprovalModal;
